import Cookie from 'services/Cookie';
import storage from 'redux-persist-indexeddb-storage';
import redirect from 'utils/redirect';
import isEmpty from 'lodash/isEmpty';
import computeUserLastCompany from 'utils/computeUserLastCompany';
import { getPasskeyEnrollmentDeferral, shouldShowPasskeyEnrollment } from 'utils/passkeyEnrollment';
import resolveSafeReturnTarget from 'utils/safeReturnTarget';
import { isWebAuthnSupported } from 'utils/webAuthn';

export { resolveSafeReturnTarget };

/**
 * Sets the token cookie with appropriate expiration options
 * @param {boolean} keepSessionOpen - Whether to keep the session open
 * @param {Object} token - The token object to store
 * @returns {Object} The cookie options used
 */
export const setTokenCookie = (keepSessionOpen, token) => {
  const options = keepSessionOpen && token.expiresAt ? { expires: new Date(token.expiresAt) } : {};
  Cookie.set('token', token, options);
  return options;
};

/**
 * Clears the auth token
 */
export const clearToken = () => {
  Cookie.removeAuthCookie('token');
};

/**
 * Clears the shared impersonation cookie
 */
export const clearImpersonationCookie = () => {
  Cookie.removeAuthCookie('impersonate');
};

/**
 * Clears all shared session cookies (logout / invalid session)
 */
export const clearSessionCookies = () => {
  clearToken();
  clearImpersonationCookie();
};

/**
 * Handles the user login process
 * @param {Object} token - Token data
 * @param {Object} user - User data
 * @param {boolean} keepSessionOpen - Whether to keep session open
 * @param {string} returnUrl - Return URL after login
 */
export const handleLogin = (
  token,
  user,
  keepSessionOpen,
  returnUrl,
  loginMethod,
  passkeyEnrollmentCompany,
  passkeyEnrollmentHandoff,
) => {
  setTokenCookie(keepSessionOpen, token);

  const safe = resolveSafeReturnTarget(returnUrl, user);

  const enrollmentOffered =
    loginMethod === 'sms' &&
    passkeyEnrollmentCompany?.id &&
    passkeyEnrollmentCompany?.subDomain &&
    passkeyEnrollmentHandoff;
  // Credential count is checked on the invitation route. A stored deferral or a
  // client that cannot create a passkey skips that route entirely.
  const showPasskeyInvitation =
    enrollmentOffered &&
    (!user?._id ||
      shouldShowPasskeyEnrollment({
        userId: user._id,
        loginMethod: 'sms',
        supported: isWebAuthnSupported(),
        credentialCount: 0,
        deferredUntil: getPasskeyEnrollmentDeferral(user._id),
      }));

  if (showPasskeyInvitation) {
    const query = new URLSearchParams({
      passkeyEnrollmentCompanyId: passkeyEnrollmentCompany.id,
    });
    if (safe?.target) {
      query.set('returnUrl', safe.target.toString());
    }
    const fragment = new URLSearchParams({ passkeyEnrollmentHandoff });
    const path = `/auth/login/passkey-enrollment?${query.toString()}#${fragment.toString()}`;
    redirect(path, passkeyEnrollmentCompany.subDomain);
    return;
  }

  const { subDomain } = computeUserLastCompany(user) || {};

  if (safe) {
    const { target } = safe;
    const path = `${target.pathname}${target.search}${target.hash}`;
    // Member-company target keeps its own subdomain; apex targets land on the default company.
    redirect(path, safe.subDomain || subDomain || '');
    return;
  }
  if (isEmpty(user.companies)) {
    redirect('/auth/login-no-invitation');
    return;
  }
  redirect('/home/messages', subDomain);
};

/**
 * Clears user data on logout
 */
export const clearUserData = () => {
  storage('ommnio').removeItem('persist:auth');
};
